import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFetchData, useListSource } from "../../components/hooks/DataSource";
import { IApiUrls } from "../../models/IApiUrls";
import { DataGrid, Popup } from "devextreme-react";
import { Button, Column, Editing } from "devextreme-react/data-grid";
import { ToolbarItem } from "devextreme-react/popup";
import { dxButtonOptions } from "devextreme/ui/button";
import { RowInsertingInfo, RowUpdatingInfo } from "devextreme/ui/data_grid";
import DirtyPrompt from "../../components/DirtyPrompt";

interface IProps {
    apiUrls: IApiUrls
}

interface IPopUpData {
    title: string,
    message: string | React.ReactNode,
    okBtnCaption: string;
    okBtnIcon: string;
    okBtnType?: 'back' | 'danger' | 'default' | 'normal' | 'success';
    onOkClicked?: () => void,
    user?: IGebruiker;
    hideCancelBtn?: boolean
}

interface IGebruiker {
    id: string
    userName: string;
    email: string;
    twoFactorEnabled: boolean;
    isBeheerder: boolean;
}

const Gebruikers = ({ apiUrls }: IProps) => {

    const fetch = useFetchData();
    const [users, setUsers] = useState<IGebruiker[]>([])
    const dsUser = useListSource(users);
    const [popupData, setPopUpData] = useState<IPopUpData>();    
    useEffect(() => {
        fetch(apiUrls.gebruikers).then(setUsers)
    }, [apiUrls.gebruikers, fetch])

    const executeAction = useCallback((endpoint: string, action: 'DELETE' | 'POST' | 'PUT', user: IGebruiker, showRespone?: (data: string) => string | React.ReactNode) => {
        return fetch(apiUrls.gebruikers + "/" + endpoint, user.userName, action, true).then(data => {
            console.log("data", endpoint, user, data);
            setPopUpData((showRespone && {
                message: showRespone(data),
                title: "Voltooid",
                user,
                okBtnCaption: "Sluiten",
                okBtnIcon: "close",
                okBtnType: "default",
                onOkClicked: () => setPopUpData(undefined),
                hideCancelBtn: true
            }) || undefined);
            return data;
        })
    }, [fetch, apiUrls.gebruikers])

    const hidePopUp = useCallback(() => setPopUpData(undefined), [])

    const updateRow = useCallback((list: IGebruiker[], updatedUtem: IGebruiker): IGebruiker[] => {
        const newList = [...list];
        const idx = newList.findIndex(u => u.id === updatedUtem.id);
        newList[idx].twoFactorEnabled = false;
        return newList;
    }, [])

    const removeUserRowClick = useCallback((e: { row: { data: IGebruiker } }) => {
        setPopUpData({
            title: "Gebruiker verwijderen",
            message: "Wult u deze gebruiker verwijderen?",
            okBtnCaption: "Verwijderen",
            okBtnIcon: "trash",
            okBtnType: "danger",
            user: e.row.data,
            onOkClicked: () => executeAction("", "DELETE", e.row.data).then((data) =>
                data === true && setUsers(prev => prev.filter(u => u.id !== e.row.data.id)))
        });
    }, [executeAction])

    const resetPwdRowClick = useCallback((e: { row: { data: IGebruiker } }) => {       
        setPopUpData({
            title: "Wachtwoord resetlink genereren",
            message: "Wult u een wachtwoord resetlink aanmaken?",
            okBtnCaption: "Reset wachtwoord",
            okBtnIcon: "key",
            okBtnType: "danger",
            user: e.row.data,
            onOkClicked: () => executeAction("reset-password", "POST", e.row.data, (data) =>
                <a href={'/identity/account/ResetPassword?code=' + data}>Wachtwoord resetlink</a>)
        });
    }, [executeAction])
    
    const reset2faRowClick = useCallback((e: { row: { data: IGebruiker } }) => {        
        setPopUpData({
            title: "Two Factor resetten",
            message: "Wult u doorgaan met het resetten van de Two Factor authenticatie?",
            okBtnCaption: "Reset 2FA",
            okBtnIcon: "checklist",
            okBtnType: "danger",
            user: e.row.data,
            onOkClicked: () => executeAction("reset-2fa", "POST", e.row.data).then(data =>
                data === "true" && setUsers(prev => updateRow(prev, { ...e.row.data, twoFactorEnabled: false })))
            //}))
            //onOkClicked: () => executeAction("reset-2fa", "POST", e.row.data).then(data => data === "true" && setUsers(prev => {
            //    const idx = prev.findIndex(u => u.id === e.row.data.id);
            //    prev[idx].twoFactorEnabled = false;
            //    console.log("new data", prev);
            //    return [...prev];
            //}))

        });
    }, [executeAction, updateRow])

    const popupOkBtnOptions = useMemo((): dxButtonOptions => ({
        onClick: popupData?.onOkClicked,
        icon: popupData?.okBtnIcon,
        text: popupData?.okBtnCaption,
        type: popupData?.okBtnType        
    }), [popupData])

    const popupCancelBtnOptions = useMemo((): dxButtonOptions => ({
        onClick: () => setPopUpData(undefined),
        text: "Annuleren",
        icon: "chevronleft"        
    }), [])

    const reset2FaVisibleHandler = useCallback((e: { row: { isNewRow: boolean, data: IGebruiker } }) => {
        return !e.row.isNewRow && e.row.data.twoFactorEnabled
    }, [])

    const newRowHideBtnsHandler = useCallback((e: { row: { isNewRow: boolean, data: IGebruiker } }) => {
        return !e.row.isNewRow;
    }, [])

    const newRowShowBtnsHandler = useCallback((e: { row: { isNewRow: boolean, data: IGebruiker } }) => {
        return e.row.isNewRow;
    }, [])

    const rowInsertingHandler = useCallback((e: RowInsertingInfo<IGebruiker>) => {
        console.log("inserting", e);
        e.cancel = fetch(apiUrls.gebruikers, e.data, "POST").then(d => {
            setUsers(prev => [...prev, d]);
        });

    }, [fetch, apiUrls.gebruikers])

    const rowUpdatingHandler = useCallback((e: RowUpdatingInfo<IGebruiker>) => {
        console.log("update row", e);
        e.cancel = fetch(apiUrls.gebruikers + "/" + e.oldData.id, { ...e.oldData, ...e.newData }, "PUT").then(d => {
            setUsers(prev => updateRow(prev,d));
        });
    }, [fetch, apiUrls.gebruikers, updateRow])

    const dgRef = useRef<DataGrid<IGebruiker, string>>(null);
    const isDirtyHandler = useCallback(() => dgRef.current?.instance.hasEditData(), [])

    return <>
        <DirtyPrompt when={isDirtyHandler} />
        <h2>Gebruikers</h2>
        <DataGrid<IGebruiker, string>
            ref={dgRef}
            dataSource={dsUser}
            onRowInserting={rowInsertingHandler}
            onRowUpdating={rowUpdatingHandler}
//            onRowRemoving={ }
        >
            <Editing allowAdding allowUpdating allowDeleting mode="batch" useIcons />
            <Column dataField="userName" />
            <Column dataField="email" />
            {/*<Column dataField="twoFactorEnabled" allowEditing={false} />*/}
            <Column dataField="isBeheerder" width={125} />

            <Column type="buttons" alignment="left">
                <Button name="delete" visible={newRowShowBtnsHandler} />
                <Button icon="trash" hint="gebruiker verwijderen" onClick={removeUserRowClick} visible={newRowHideBtnsHandler} />
                <Button icon="key" hint="wachtwoord resetlink genereren" onClick={resetPwdRowClick} visible={newRowHideBtnsHandler} />
                <Button icon="checklist" hint="Twee trapsverificatie uitschakelen" visible={reset2FaVisibleHandler} onClick={reset2faRowClick} />
            </Column>
            
        </DataGrid>
        <Popup
            visible={popupData !== undefined}
            title={popupData?.title}
            height="auto"
            width="auto"
            minWidth="400px"
            minHeight="100px"
            maxWidth="80vw"
            
            //restorePosition
            dragEnabled
            dragOutsideBoundary
            closeOnOutsideClick
            showCloseButton
            onHiding={hidePopUp}>

            <ToolbarItem
                widget="dxButton"
                toolbar="bottom"
                location="after"
                visible={!popupData?.hideCancelBtn}
                options={popupCancelBtnOptions}
            />
            <ToolbarItem
                widget="dxButton"
                toolbar="bottom"
                location="after"
                options={popupOkBtnOptions}
            />
            
            <p>Gebruiker: {popupData?.user?.userName}</p>
            <p>{popupData?.message}</p>
            
        </Popup>
    </>;
}
export default Gebruikers;